import { createStore, type StoreApi } from 'zustand/vanilla';
import type { InstanceId, RoomId } from '@/core/content/ids';
import { slotKey, slotParent, type SlotKey } from '@/core/domain/placement/geometry';
import type { PlacementTarget } from '@/core/domain/placement/rules';
import type { GameSave, SlotContent } from '@/core/persistence/schema/v1';
import type { Command, CommandResult } from './commands';
import type { Dispatch } from './dispatch';

interface Entry {
  roomId: RoomId;
  undo: Command[];
  redo: Command[];
}

export interface UndoState {
  canUndo: boolean;
  canRedo: boolean;
}

/** Where an instance is right now, as a placement target (so it can be put back exactly there). */
function targetOf(state: GameSave, instanceId: InstanceId): PlacementTarget | null {
  const inst = state.inventory.instances[instanceId];
  if (inst?.location.kind !== 'placed') return null;
  const roomId = inst.location.roomId;
  const room = state.rooms[roomId];
  const p = room.placements[instanceId];
  if (p) {
    return p.layer === 'wall'
      ? { layer: 'wall', roomId, wall: p.wall, offset: p.offset }
      : { layer: p.layer, roomId, x: p.x, y: p.y, rotation: p.rotation };
  }
  for (const [key, c] of Object.entries(room.slots) as [SlotKey, SlotContent][]) {
    if (c.kind === 'instance' && c.instanceId === instanceId) {
      return {
        layer: 'surface',
        roomId,
        parentInstanceId: slotParent(key),
        slotIndex: Number(key.split('#')[1]),
      };
    }
  }
  return null;
}

/** Decor sitting on an item's surface (restored with it when a store is undone). */
function childrenOf(state: GameSave, instanceId: InstanceId): { id: InstanceId; target: PlacementTarget }[] {
  const out: { id: InstanceId; target: PlacementTarget }[] = [];
  for (const room of Object.values(state.rooms)) {
    for (const [key, c] of Object.entries(room.slots) as [SlotKey, SlotContent][]) {
      if (c.kind !== 'instance' || slotParent(key) !== instanceId) continue;
      const target = targetOf(state, c.instanceId);
      if (target) out.push({ id: c.instanceId, target });
    }
  }
  return out;
}

/** Inverse commands computed BEFORE the command runs (content-schemas §13.4). */
function inverseOf(state: GameSave, cmd: Command): { roomId: RoomId; undo: Command[] } | null {
  if (cmd.type === 'placeItem') {
    const before = targetOf(state, cmd.instanceId);
    return {
      roomId: cmd.target.roomId,
      undo: before
        ? [{ type: 'placeItem', instanceId: cmd.instanceId, target: before }]
        : [{ type: 'storeItem', instanceId: cmd.instanceId }],
    };
  }
  if (cmd.type === 'rotateItem' || cmd.type === 'storeItem') {
    const before = targetOf(state, cmd.instanceId);
    if (!before) return null;
    const restore: Command[] = [{ type: 'placeItem', instanceId: cmd.instanceId, target: before }];
    if (cmd.type === 'storeItem') {
      for (const child of childrenOf(state, cmd.instanceId)) {
        restore.push({ type: 'placeItem', instanceId: child.id, target: child.target });
      }
    }
    return { roomId: before.roomId, undo: restore };
  }
  if (cmd.type === 'displayProduct') {
    // Shelving one unit (drag or "Xếp đầy kệ"): undo takes it back to the bag.
    const key = slotKey(cmd.parentInstanceId, cmd.slotIndex);
    return { roomId: cmd.roomId, undo: [{ type: 'undisplayProduct', roomId: cmd.roomId, slotKey: key }] };
  }
  return null;
}

/**
 * Per-room undo/redo for organizing commands. Undo/redo run through the normal dispatcher with origin
 * 'undo'/'redo', so they are validated and never count towards stats or achievements.
 */
export class UndoManager {
  readonly ui: StoreApi<UndoState> = createStore<UndoState>(() => ({ canUndo: false, canRedo: false }));
  private undoStack: Entry[] = [];
  private redoStack: Entry[] = [];
  private room: RoomId = 'home';

  constructor(
    private readonly dispatch: Dispatch,
    private readonly getState: () => GameSave,
    private readonly maxDepth = 50,
  ) {}

  /** Dispatch an organizing command as the player, recording how to undo it. */
  run(cmd: Command): CommandResult {
    const inverse = inverseOf(this.getState(), cmd);
    const result = this.dispatch(cmd, 'player');
    if (result.ok && inverse) {
      this.undoStack.push({ roomId: inverse.roomId, undo: inverse.undo, redo: [cmd] });
      if (this.undoStack.length > this.maxDepth) this.undoStack.shift();
      this.redoStack = [];
      this.publish();
    }
    return result;
  }

  undo(): boolean {
    const entry = this.undoStack.pop();
    if (!entry) return false;
    const redo = this.apply(entry.undo, 'undo');
    if (redo) this.redoStack.push(entry);
    this.publish();
    return redo;
  }

  redo(): boolean {
    const entry = this.redoStack.pop();
    if (!entry) return false;
    // Recompute the inverse against the current state, in case things moved.
    const first = entry.redo[0];
    const inverse = first ? inverseOf(this.getState(), first) : null;
    const ok = this.apply(entry.redo, 'redo');
    if (ok && inverse) this.undoStack.push({ roomId: entry.roomId, undo: inverse.undo, redo: entry.redo });
    this.publish();
    return ok;
  }

  /** Changing room (or an economy action touching it) clears the history (content-schemas §13.4). */
  setRoom(roomId: RoomId): void {
    if (roomId === this.room) return;
    this.room = roomId;
    this.clear();
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.publish();
  }

  private apply(commands: readonly Command[], origin: 'undo' | 'redo'): boolean {
    for (const c of commands) {
      const r = this.dispatch(c, origin);
      // A step that no longer fits (cell now taken) is dropped quietly; nothing is lost.
      if (!r.ok) return false;
    }
    return true;
  }

  private publish(): void {
    this.ui.setState({ canUndo: this.undoStack.length > 0, canRedo: this.redoStack.length > 0 });
  }
}
